<template>
  <v-container max-width="80%" class="mx-auto py-8">
    <div class="text-overline font-weight-semibold mb-1">Submit a New Listing</div>
    <h2 class="text-h5 font-weight-bold mb-6">Enter Sailing Details</h2>

    <v-row align="start" style="--v-gutter-x: 28px; --v-gutter-y: 28px;">
      <!-- Left rail -->
      <v-col cols="12" md="3">
        <PortStopsCard :foundItinerary="foundItinerary" />
      </v-col>

      <!-- Right column -->
      <v-col cols="12" md="9">
        <!-- Progress (keep consistent with Step 2) -->
        <v-card class="mb-6" elevation="2" rounded="lg">
          <v-card-text>
            <div class="d-flex align-center justify-space-between mb-2">
              <div class="text-subtitle-1 font-weight-medium">Step {{ currentStep }} of {{ totalSteps }}</div>
              <div class="text-caption">{{ Math.round(progressPercentage) }}%</div>
            </div>
            <v-progress-linear :model-value="progressPercentage" height="12" rounded color="secondary" />
          </v-card-text>
        </v-card>

        <!-- Confirmation -->
        <v-card class="mb-6" elevation="3" rounded="lg">
          <v-card-title class="text-subtitle-1 font-weight-bold">Confirmation</v-card-title>
          <v-divider />
          <v-card-text>
            <v-row class="mb-2" dense>
              <v-col cols="12" md="6">
                <div class="text-subtitle-2 font-weight-medium mb-2">Suite Details</div>
                <v-list density="comfortable" lines="one">
                  <v-list-item title="Code" :subtitle="currentListing.selectedCode || '—'" />
                  <v-list-item title="Description" :subtitle="currentListing.description || '—'" />
                  <v-list-item title="Room Type" :subtitle="currentListing.roomType || '—'" />
                  <v-list-item title="Guest Limit" :subtitle="currentListing.guestLimit || '—'" />
                  <v-list-item title="Cabin Number" :subtitle="currentListing.cabinNumber || 'TBD'" />
                </v-list>
              </v-col>

              <v-col cols="12" md="6">
                <div class="text-subtitle-2 font-weight-medium mb-2">Booking Info</div>
                <v-list density="comfortable" lines="one">
                  <v-list-item title="Tips Included" :subtitle="capitalize(currentListing.tipsIncluded) || '—'" />
                  <v-list-item title="Name Swaps Allowed" :subtitle="capitalize(currentListing.nameSwaps) || '—'" />
                  <v-list-item title="Promote Listing" :subtitle="capitalize(currentListing.promote) || '—'" />
                  <v-list-item title="Notes" :subtitle="currentListing.notes || 'None'" />
                </v-list>
              </v-col>
            </v-row>

            <v-row class="mb-2" dense>
              <v-col cols="12" md="6">
                <div class="text-subtitle-2 font-weight-medium mb-2">Pricing</div>
                <v-list density="comfortable" lines="one">
                  <v-list-item title="Cruise Fare" :subtitle="`$${asMoney(fareNum)}`" />
                  <v-list-item title="Taxes" :subtitle="`$${asMoney(taxesNum)}`" />
                  <v-list-item title="Deposit" :subtitle="`$${asMoney(depositNum)}`" />
                  <v-list-item title="Total Cost" :subtitle="totalCostNum ? `$${asMoney(totalCostNum)}` : 'TBD'" />
                </v-list>
              </v-col>

              <v-col cols="12" md="6">
                <div class="text-subtitle-2 font-weight-medium mb-2">Payment & Dates</div>
                <v-list density="comfortable" lines="one">
                  <v-list-item title="Final Payment Due"
                    :subtitle="formatDate(currentListing.finalPaymentDate) || '—'" />
                  <v-list-item title="Remove Listing On" :subtitle="formatDate(currentListing.removeDate) || '—'" />
                </v-list>
              </v-col>
            </v-row>

            <v-row class="mb-2" dense>
              <v-col cols="12" md="6">
                <div class="text-subtitle-2 font-weight-medium mb-2">Included Perks</div>
                <div v-if="(currentListing.perks || []).length">
                  <v-chip-group multiple :model-value="currentListing.perks">
                    <v-chip v-for="perk in currentListing.perks" :key="perk" class="ma-1" variant="elevated">
                      {{ formatPerk(perk) }}
                    </v-chip>
                  </v-chip-group>
                </div>
                <div v-else class="text-medium-emphasis">No perks selected.</div>
              </v-col>

              <v-col cols="12" md="6">
                <div class="text-subtitle-2 font-weight-medium mb-2">Cancellation Policy</div>
                <div v-if="hasCancellation">
                  <v-list density="compact" lines="one">
                    <v-list-item v-for="(c, i) in currentListing.cancellation" :key="i"
                      :title="formatDate(c.date) || 'TBD'" :subtitle="`${c.percentage || 'TBD'}% penalty`" />
                  </v-list>
                </div>
                <div v-else class="text-medium-emphasis">No cancellation dates provided yet.</div>
              </v-col>
            </v-row>

            <v-divider class="my-4" />

            <!-- Payout Summary -->
            <div>
              <div class="text-h6 text-center font-weight-bold mb-4">Payout Summary</div>
              <v-row class="text-center" justify="center">
                <v-col cols="12" sm="6" md="5">
                  <div class="text-body-2">Total Fare</div>
                  <div class="text-subtitle-1 font-weight-semibold">${{ asMoney(fareNum) }}</div>
                </v-col>
                <v-col cols="12" sm="6" md="5">
                  <div class="text-body-2">Our Fee ({{ payoutFeePercentage }}%)</div>
                  <div class="text-subtitle-1 text-error font-weight-semibold">-${{ asMoney(payoutFee) }}</div>
                </v-col>
              </v-row>
              <v-divider class="my-3" />
              <div class="text-center text-success font-weight-bold" style="font-size: 1.6rem;">
                Estimated Payout: ${{ asMoney(estimatedPayout) }}
              </div>
            </div>
          </v-card-text>
        </v-card>

        <!-- Submit -->
        <v-card elevation="2" rounded="lg">
          <v-card-text class="text-center">
            <v-checkbox v-model="agreed" color="primary" hide-details
              label="I confirm these details are accurate and agree to the platform terms and service fees." />
            <v-btn color="primary" size="large" class="mt-2" :disabled="!agreed" @click="submitListing">
              Submit Listing
            </v-btn>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import PortStopsCard from '@/components/PortStopsCard.vue';
import { supabase } from '@/supabaseClient';
import { useNotifier } from '@/composables/useNotifier'
const { sendNotification } = useNotifier()

export default {
  name: 'StepThreeVuetify',
  components: { PortStopsCard },
  props: {
    foundItinerary: Object,
    currentListing: Object,
    // allow overriding if you keep a global step tracker
    currentStep: { type: Number, default: 4 },
    totalSteps: { type: Number, default: 4 }
  },
  data() {
    return { agreed: false };
  },
  computed: {
    progressPercentage() {
      return (this.currentStep / this.totalSteps) * 100;
    },
    hasCancellation() {
      return this.currentListing?.cancellation?.some(c => c.date || c.percentage);
    },
    // numeric coercions
    fareNum() { return this.asNumber(this.currentListing?.cruiseFare); },
    taxesNum() { return this.asNumber(this.currentListing?.taxes); },
    depositNum() { return this.asNumber(this.currentListing?.deposit); },
    totalCostNum() { return this.asNumber(this.currentListing?.totalCost); },

    payoutFeePercentage() {
      var baseFee = 3.0;
      if (this.$store.state.user.membership_plan === 'enhanced')
        baseFee = 1.75;
      else if (this.$store.state.user.membership_plan === 'premium')
        baseFee = 1.25;
      const promoteFee = (this.currentListing.promote || '').toString().toLowerCase() === 'yes' ? 1.5 : 0;
      return baseFee + promoteFee;
    },
    payoutFee() {
      return this.fareNum * (this.payoutFeePercentage / 100);
    },
    estimatedPayout() {
      return Math.max(0, this.fareNum - this.payoutFee);
    }
  },
  methods: {
    // helpers
    asNumber(val) {
      const n = Number(val);
      return Number.isFinite(n) ? n : 0;
    },
    asMoney(val) {
      return this.asNumber(val).toFixed(2);
    },
    formatDate(dateStr) {
      if (!dateStr) return '';
      const opts = { year: 'numeric', month: 'short', day: 'numeric' };
      return new Date(dateStr).toLocaleDateString(undefined, opts);
    },
    formatPerk(perk) {
      const map = {
        onboardCredit50: 'Onboard Credit - $50',
        onboardCredit100: 'Onboard Credit - $100',
        other: 'Other',
        specialtyDining: 'Specialty Dining',
        prepaidGratuity: 'Prepaid Gratuity',
        beveragePackage: 'Beverage Package',
        internetPackage: 'Internet Package',
        shoreExcursionCredit: 'Shore Excursion Credit'
      };
      return map[perk] || perk;
    },
    capitalize(val) {
      const s = val == null ? '' : String(val);
      return s.charAt(0).toUpperCase() + s.slice(1);
    },
    generateShortCode(length = 8) {
      const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
      let out = '';
      for (let i = 0; i < length; i++) out += chars[Math.floor(Math.random() * chars.length)];
      return out;
    },

    async submitListing() {
      if (!this.agreed) return;

      try {
        const uid = this.$store.state.user.uid;

        const platform_fee = this.totalCostNum * 0.05;
        const total_guest_price = this.totalCostNum + platform_fee;

        const payload = {
          selected_code: this.currentListing.selectedCode,
          description: this.currentListing.description,
          room_type: this.currentListing.roomType,
          cabin_number: this.currentListing.cabinNumber,
          guest_limit: this.currentListing.guestLimit,
          tips_included: this.currentListing.tipsIncluded,
          name_swaps: this.currentListing.nameSwaps,
          cruise_fare: this.fareNum,
          taxes: this.taxesNum,
          perks: this.currentListing.perks,
          total_cost: this.totalCostNum,
          deposit: this.depositNum,
          final_payment_date: this.currentListing.finalPaymentDate || null,
          cancellation: this.currentListing.cancellation || [],
          remove_date: this.currentListing.removeDate || null,
          notes: this.currentListing.notes,
          promote: this.currentListing.promote,
          sailing_id: this.currentListing.sailingID,
          itinerary: this.currentListing.itinerary,
          status: 'pending',
          service_fee: this.payoutFee,
          platform_fee,
          total_guest_price,
          uid,
          booking_id: this.generateShortCode(),
          created_at: new Date().toISOString()
        };

        const { data, error } = await supabase.from('listings').insert([payload]);
        if (error) {
          console.error('Error uploading listing:', error);
          alert('Something went wrong.');
        } else {
          alert('Listing submitted successfully!');
          await sendNotification({ type: 'listing_posted', forAdmin: true, listingId: data.id })
          this.$router.push('/dashboard/home');
        }
      } catch (err) {
        console.error('Unexpected error:', err);
        alert('Something went wrong.');
      }
    }
  }
};
</script>

<style scoped>
/* keep buttons consistent with Step 2 if needed */
</style>
